import { format, addDays, subDays, isWithinInterval, areIntervalsOverlapping } from 'date-fns';
// TODO: Adapter pour utiliser les contextes API
// import { onCallsDB } from '../db/database';

// Types d'erreurs de validation
export const ValidationErrorType = {
  INSUFFICIENT_REST: 'INSUFFICIENT_REST',
  ABSENCE_CONFLICT: 'ABSENCE_CONFLICT',
  JTL_WARNING: 'JTL_WARNING'
};

// Type d'absence JTL
const JTL_ABSENCE_TYPE = 'JTL';

/**
 * Vérifie si une période d'astreinte respecte la règle des 2 semaines de repos
 */
const checkRestPeriod = async (technicianId, startDate, endDate, allOnCalls, excludeOnCallId = null) => {
  const technicianOnCalls = allOnCalls.filter(
    onCall => onCall.technicianId === parseInt(technicianId) && 
    (!excludeOnCallId || onCall.id !== excludeOnCallId)
  );

  // Vérifier 2 semaines avant et après la période proposée
  const minRestStart = subDays(new Date(startDate), 14);
  const minRestEnd = addDays(new Date(endDate), 14);

  const conflictingOnCall = technicianOnCalls.find(onCall => {
    const onCallStart = new Date(onCall.startDate);
    const onCallEnd = new Date(onCall.endDate);
    
    // 🔧 CORRECTION : Ne pas bloquer si on essaie d'ajouter une compétence sur EXACTEMENT la même période
    // Cela arrive quand on supprime une compétence puis on la rajoute, ou quand on ajoute une compétence
    // à un technicien déjà d'astreinte sur cette période
    const sameStartDate = onCall.startDate === startDate;
    const sameEndDate = onCall.endDate === endDate;
    
    if (sameStartDate && sameEndDate) {
      console.log(`✅ Même période exacte détectée - pas de conflit de repos (${startDate} - ${endDate})`);
      return false; // Pas de conflit car c'est la même période
    }

    return isWithinInterval(onCallStart, { start: minRestStart, end: minRestEnd }) ||
           isWithinInterval(onCallEnd, { start: minRestStart, end: minRestEnd }) ||
           isWithinInterval(new Date(startDate), { start: onCallStart, end: onCallEnd }) ||
           isWithinInterval(new Date(endDate), { start: onCallStart, end: onCallEnd });
  });

  if (conflictingOnCall) {
    return {
      isValid: false,
      type: ValidationErrorType.INSUFFICIENT_REST,
      message: 'Le technicien doit avoir un repos minimum de 2 semaines entre chaque période d\'astreinte.'
    };
  }

  return { isValid: true };
};

/**
 * Déterminer la sévérité et le message selon le type d'absence
 */
const getAbsenceSeverityAndMessage = (absenceType) => {
  const type = absenceType?.toUpperCase();
  
  switch (type) {
    case 'JTL':
      return {
        severity: 'warning',
        message: `JTL détecté - Compatible avec une astreinte mais à noter`,
        canOverride: true
      };
    
    case 'RECUP_ASTREINTE':
      return {
        severity: 'warning',
        message: `Récup astreinte détectée - Compatible avec une astreinte mais à noter`,
        canOverride: true
      };
    
    case 'CA':
    case 'CP':
      return {
        severity: 'critical',
        message: `Conflit avec congés payés - BLOQUANT`,
        canOverride: false
      };
    
    case 'ARRET MALADIE':
    case 'MALADIE':
      return {
        severity: 'critical',
        message: `Conflit avec arrêt maladie - BLOQUANT`,
        canOverride: false
      };
    
    case 'FORMATION':
      return {
        severity: 'critical',
        message: `Conflit avec formation - BLOQUANT`,
        canOverride: false
      };
    
    case 'AUTRE':
      return {
        severity: 'critical',
        message: `Absence de type "AUTRE" détectée - Veuillez spécifier le type d'absence avant de planifier`,
        canOverride: false
      };
    
    case 'TPS':
      return {
        severity: 'critical',
        message: `Conflit avec temps partiel - BLOQUANT`,
        canOverride: false
      };
    
    default:
      return {
        severity: 'critical',
        message: `Conflit avec absence de type "${absenceType}" - À vérifier`,
        canOverride: false
      };
  }
};

/**
 * Vérifie les conflits avec les absences
 */
const checkAbsenceConflicts = async (technicianId, startDate, endDate, allAbsences) => {
  const absences = allAbsences.filter(absence => 
    absence.technicianId === parseInt(technicianId)
  );
  
  console.log(`🔍 DEBUG checkAbsenceConflicts pour technicien ${technicianId}:`);
  console.log(`   - Période astreinte: ${startDate} → ${endDate}`);
  console.log(`   - Total absences: ${allAbsences.length}`);
  console.log(`   - Absences du technicien: ${absences.length}`);
  console.log(`🚨 PARAMÈTRES REÇUS:`, { technicianId, startDate, endDate });
  absences.forEach(abs => {
    console.log(`     * ${abs.type} du ${abs.startDate} au ${abs.endDate} | bypass: ${abs.bypassAbsenceValidation} | ID: ${abs.id}`);
  });
  
  console.log(`🚨 ATTENTION: On va analyser seulement les absences qui chevauchent vraiment...`);

  // Créer les dates en forçant le fuseau horaire local (Paris)
  const onCallInterval = {
    start: new Date(startDate + 'T00:00:00'),
    end: new Date(endDate + 'T23:59:59')
  };
  
  console.log(`📅 INTERVALLE ASTREINTE:`, {
    start: onCallInterval.start.toISOString(),
    startLocal: onCallInterval.start.toLocaleString('fr-FR'),
    end: onCallInterval.end.toISOString(), 
    endLocal: onCallInterval.end.toLocaleString('fr-FR')
  });
  
  if (!absences || absences.length === 0) {
    return { isValid: true };
  }

  const conflictingAbsences = absences.filter(absence => {
    const absenceInterval = {
      start: new Date(absence.startDate + 'T00:00:00'),
      end: new Date(absence.endDate + 'T23:59:59')
    };

    console.log(`🔍 ANALYSE ABSENCE ${absence.id}:`, {
      type: absence.type,
      dates: `${absence.startDate} → ${absence.endDate}`,
      interval: {
        start: absenceInterval.start.toISOString(),
        startLocal: absenceInterval.start.toLocaleString('fr-FR'),
        end: absenceInterval.end.toISOString(),
        endLocal: absenceInterval.end.toLocaleString('fr-FR')
      },
      bypass: absence.bypassAbsenceValidation
    });

    const isOverlapping = areIntervalsOverlapping(onCallInterval, absenceInterval, { inclusive: true });
    console.log(`   ❓ Chevauchement: ${isOverlapping ? 'OUI' : 'NON'}`);
    console.log(`   🔗 Comparaison détaillée:`, {
      onCall: `${onCallInterval.start.toLocaleDateString('fr-FR')} - ${onCallInterval.end.toLocaleDateString('fr-FR')}`,
      absence: `${absenceInterval.start.toLocaleDateString('fr-FR')} - ${absenceInterval.end.toLocaleDateString('fr-FR')}`,
      inclusive: true
    });
    
    if (isOverlapping) {
      console.log(`   📅 CONFLIT détecté avec absence ${absence.type} (${absence.startDate} → ${absence.endDate}) | bypass: ${absence.bypassAbsenceValidation}`);
    }
    return isOverlapping;
  });

  if (conflictingAbsences.length > 0) {
    // Analyser chaque type d'absence pour déterminer la réaction
    // En excluant les absences avec bypass activé
    const criticalAbsences = [];
    const warningAbsences = [];
    
    conflictingAbsences.forEach(absence => {
      console.log(`   🔄 Traitement absence ID:${absence.id} | type:${absence.type} | bypass:${absence.bypassAbsenceValidation} | dates:${absence.startDate}-${absence.endDate}`);
      
      // Si l'absence a un bypass activé, elle n'est pas bloquante
      if (absence.bypassAbsenceValidation === true) {
        console.log(`   ✅ BYPASS ACTIVÉ - Absence ignorée: ${absence.type} du ${absence.startDate} au ${absence.endDate}`);
        return; // Ignorer cette absence
      }
      
      const { severity } = getAbsenceSeverityAndMessage(absence.type);
      console.log(`   📊 Absence ${absence.type} classée comme: ${severity}`);
      
      if (severity === 'critical') {
        criticalAbsences.push(absence);
        console.log(`   ❌ Ajoutée aux absences CRITIQUES`);
      } else {
        warningAbsences.push(absence);
        console.log(`   ⚠️ Ajoutée aux absences WARNING`);
      }
    });

    // Si il y a au moins une absence critique, c'est bloquant
    if (criticalAbsences.length > 0) {
      const blockingAbsence = criticalAbsences[0];
      const { message, canOverride } = getAbsenceSeverityAndMessage(blockingAbsence.type);
      
      console.log(`   ❌ VALIDATION ÉCHOUE - Absence critique bloquante: ${blockingAbsence.type}`);
      
      return {
        isValid: false,
        type: ValidationErrorType.ABSENCE_CONFLICT,
        message: `${message} (${format(new Date(blockingAbsence.startDate), 'dd/MM/yyyy')})`,
        canOverride: canOverride
      };
    }

    // Si il n'y a que des absences en warning (JTL, RECUP_ASTREINTE)
    if (warningAbsences.length > 0) {
      const absenceTypes = warningAbsences.map(abs => abs.type.toUpperCase()).join(', ');
      const dates = warningAbsences
        .map(absence => format(new Date(absence.startDate), 'dd/MM/yyyy'))
        .join(', ');
      
      return {
        isValid: false,
        type: ValidationErrorType.JTL_WARNING,
        message: `Le technicien a des absences compatibles (${absenceTypes}) pendant cette période (${dates}). Voulez-vous quand même créer l'astreinte ?`,
        canOverride: true
      };
    }
  }

  console.log(`   ✅ VALIDATION RÉUSSIE - Aucune absence bloquante détectée`);
  return { isValid: true };
};

/**
 * Valide une période d'astreinte
 * @param {Object} onCall - L'astreinte à valider
 * @param {Array} allOnCalls - Toutes les astreintes existantes
 * @param {Array} allAbsences - Toutes les absences existantes
 * @param {number} excludeOnCallId - ID de l'astreinte à exclure (pour les modifications)
 */
export const validateOnCall = async (onCall, allOnCalls, allAbsences, excludeOnCallId = null, bypassAbsenceValidation = false) => {
  try {
    // Vérifier la période de repos
    const restValidation = await checkRestPeriod(
      onCall.technicianId,
      onCall.startDate,
      onCall.endDate,
      allOnCalls,
      excludeOnCallId
    );
    
    if (!restValidation.isValid) {
      return restValidation;
    }

    // Vérifier les conflits d'absence (sauf si bypass activé)
    if (!bypassAbsenceValidation) {
      console.log(`🚨 APPEL checkAbsenceConflicts avec onCall:`, {
        technicianId: onCall.technicianId,
        startDate: onCall.startDate,
        endDate: onCall.endDate,
        fullOnCall: onCall
      });
      
      const absenceValidation = await checkAbsenceConflicts(
        onCall.technicianId,
        onCall.startDate,
        onCall.endDate,
        allAbsences
      );
      
      if (!absenceValidation.isValid) {
        return absenceValidation;
      }
    }

    return { isValid: true };
  } catch (error) {
    console.error('Erreur lors de la validation:', error);
    return {
      isValid: false,
      type: 'ERROR',
      message: 'Une erreur est survenue lors de la validation de l\'astreinte.'
    };
  }
}; 